import { Link } from "react-router-dom";
import { Compass, ArrowLeft } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import { SITE } from "@/content/site";
import { useHeadMeta } from "@/lib/head";

/**
 * Shown for any unrecognised URL.
 *
 * This replaces the previous wildcard route, which silently redirected every
 * unknown path to `/`. That behaviour hid broken links from visitors and
 * produced soft 404s: crawlers saw homepage content served at a nonsense URL
 * and could index it as a duplicate.
 *
 * Known limitation. React cannot set an HTTP status code, so this page still
 * arrives with whatever status the host returns. Making unknown URLs return a
 * real 404 needs hosting configuration, which is documented in
 * `docs/deployment-seo-requirements.md`. The `noindex` directive below limits
 * the damage until that lands.
 */
export default function NotFound() {
  useHeadMeta({
    title: "Page not found — SEO Signals",
    robots: "noindex, nofollow",
  });

  return (
    <div className="min-h-screen bg-offwhite text-navy font-sans flex flex-col">
      <div className="ss-home" style={{ minHeight: 0 }}><PublicHeader /></div>

      <main className="flex-1 grid place-items-center px-5 py-20">
        <div className="max-w-lg text-center">
          <div className="mx-auto grid place-items-center h-14 w-14 rounded-xl bg-emerald/10 text-emerald">
            <Compass size={26} aria-hidden />
          </div>
          <h1 className="mt-6 font-heading font-bold tracking-tight text-3xl sm:text-4xl text-navy">
            We can't find that page
          </h1>
          <p className="mt-4 text-navy/70 leading-relaxed">
            The link may be out of date, or the address may have a typo in it. Nothing is wrong with
            your account.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/"
              data-testid="notfound-home-link"
              className="inline-flex items-center gap-2 rounded-md bg-emerald px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-dark transition-colors"
            >
              <ArrowLeft size={16} aria-hidden />
              Back to the homepage
            </Link>
            <Link
              to="/dashboard"
              data-testid="notfound-dashboard-link"
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-navy hover:bg-slate-50 transition-colors"
            >
              Go to your dashboard
            </Link>
          </div>

          <p className="mt-8 text-sm text-navy/55">
            Still stuck? Email{" "}
            <a
              href={`mailto:${SITE.contactEmail}`}
              className="font-semibold text-emerald-dark hover:underline"
            >
              {SITE.contactEmail}
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
